import type {
  ExitPendingApprovalRow,
  ExitPendingListPayload,
} from "./types";

const emptyList = (): ExitPendingListPayload => ({
  data: [],
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 0,
});

function unwrapEnvelope<T>(raw: unknown): T | undefined {
  if (raw === null || raw === undefined) return undefined;
  if (
    typeof raw === "object" &&
    raw !== null &&
    "data" in raw &&
    "success" in raw
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/**
 * Normalizes GET /v2/exit/pending-approvals (and similar) into a single list shape.
 */
export function normalizeExitPendingListPayload(
  raw: unknown,
): ExitPendingListPayload {
  const inner = unwrapEnvelope<unknown>(raw);
  if (inner == null) return emptyList();

  /** Backend may return `MyExitResponseDtoV2[]` (bare or inside envelope). */
  if (Array.isArray(inner)) {
    const data = inner as ExitPendingApprovalRow[];
    const n = data.length;
    return {
      data,
      total: n,
      page: 1,
      limit: 10,
      totalPages: Math.max(1, Math.ceil(Math.max(n, 1) / 10)),
    };
  }

  if (typeof inner !== "object") return emptyList();

  const o = inner as Record<string, unknown>;

  if (
    Array.isArray(o.data) &&
    o.pagination &&
    typeof o.pagination === "object"
  ) {
    const pag = o.pagination as Record<string, unknown>;
    const total = Number(pag.total ?? 0);
    const limit = Number(pag.limit ?? 10);
    const page = Number(pag.page ?? 1);
    return {
      data: o.data as ExitPendingApprovalRow[],
      total,
      page,
      limit,
      totalPages: Number(
        pag.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, limit))),
      ),
    };
  }

  if (Array.isArray(o.data) && typeof o.total === "number") {
    const limit = Number(o.limit ?? 10);
    const total = Number(o.total);
    return {
      data: o.data as ExitPendingApprovalRow[],
      total,
      page: Number(o.page ?? 1),
      limit,
      totalPages: Number(
        o.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, limit))),
      ),
    };
  }

  /**
   * `{ data: MyExitResponseDtoV2[] }` without numeric `total` / pagination.
   * Previously fell through and returned an empty list.
   */
  if (Array.isArray(o.data)) {
    const data = o.data as ExitPendingApprovalRow[];
    const n = data.length;
    return {
      data,
      total: n,
      page: 1,
      limit: 10,
      totalPages: Math.max(1, Math.ceil(Math.max(n, 1) / 10)),
    };
  }

  const listKeys = ["items", "results", "exits", "records"] as const;
  for (const k of listKeys) {
    const arr = o[k];
    if (Array.isArray(arr)) {
      const data = arr as ExitPendingApprovalRow[];
      const n = data.length;
      return {
        data,
        total: n,
        page: 1,
        limit: 10,
        totalPages: Math.max(1, Math.ceil(Math.max(n, 1) / 10)),
      };
    }
  }

  const nested = o.data as Record<string, unknown> | null | undefined;
  if (nested && typeof nested === "object" && Array.isArray(nested.data)) {
    const pag = nested.pagination as Record<string, number> | undefined;
    const total = Number(nested.total ?? pag?.total ?? 0);
    const limit = Number(nested.limit ?? pag?.limit ?? 10);
    return {
      data: nested.data as ExitPendingApprovalRow[],
      total,
      page: Number(nested.page ?? pag?.page ?? 1),
      limit,
      totalPages: Number(
        nested.totalPages ??
          pag?.totalPages ??
          Math.max(1, Math.ceil(total / Math.max(1, limit))),
      ),
    };
  }

  return emptyList();
}
